import { useState, useEffect, useCallback, useRef } from 'react';

// Custom event name for closing all context menus
const CLOSE_ALL_CONTEXT_MENUS_EVENT = 'sidebar:closeAllContextMenus';

/**
 * Hook for managing context menu state.
 * Handles showing/hiding menu and click-outside dismissal.
 * Uses a custom event to ensure only one context menu is open at a time.
 * 
 * @returns {Object} Context menu state and handlers
 */
const useContextMenu = () => {
  const [contextMenu, setContextMenu] = useState(null);
  const menuIdRef = useRef(Math.random().toString(36).substr(2, 9));

  // Close context menu on any click or when another menu opens
  useEffect(() => {
    if (contextMenu) {
      const handleClick = () => setContextMenu(null);
      const handleCloseAll = (e) => {
        // Close this menu if a different menu is opening
        if (e.detail?.menuId !== menuIdRef.current) {
          setContextMenu(null);
        }
      };
      
      document.addEventListener('click', handleClick);
      document.addEventListener(CLOSE_ALL_CONTEXT_MENUS_EVENT, handleCloseAll);
      
      return () => {
        document.removeEventListener('click', handleClick);
        document.removeEventListener(CLOSE_ALL_CONTEXT_MENUS_EVENT, handleCloseAll);
      };
    }
  }, [contextMenu]);

  const openContextMenu = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Dispatch event to close all other context menus first
    document.dispatchEvent(new CustomEvent(CLOSE_ALL_CONTEXT_MENUS_EVENT, {
      detail: { menuId: menuIdRef.current }
    }));
    
    setContextMenu({ x: e.clientX, y: e.clientY });
  }, []);

  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  return {
    contextMenu,
    openContextMenu,
    closeContextMenu,
  };
};

export default useContextMenu;
